import React, { lazy, Suspense, useEffect, useLayoutEffect } from "react";
import { Navigate, useRoutes, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ICPPageLoader from "./components/loader/ICPPageLoader";
import { isSeller, shouldRedirectToOrdersOnLogin } from "./utils/userTypes";
import { getLoginPath } from "./utils/authPortal";
import { preloadVipsDashboard } from "./utils/preloadDashboard";
import {
  loggedOutOrderLinkPath,
  readOrderIdQuery,
  rememberSellerOrderDeepLink,
  sellerOrdersDeepLink,
} from "./pages/orders/utils/orderDeepLink";

import LoginPage from "./pages/auth/login/LoginPage";
import VipLoginPage from "./pages/auth/vips/VipLoginPage";
import {
  loadApps,
  loadBreakdownPage,
  loadCart,
  loadCatalogPage,
  loadCountdownAdmin,
  loadDashboardAppPage,
  loadCommunityNotificationsPage,
  loadDashboardLayout,
  loadGrowthMaterial,
  loadInvoices,
  loadMockupLibrary,
  loadOrders,
  loadProductPage,
  loadProductsPage,
  loadProfile,
  loadScopes,
  loadSettings,
  loadShipstationCredentials,
  loadStores,
  loadSubscriptionManagement,
  loadTrainings,
  loadTrainingsDetail,
  loadTutorialDetail,
  loadTutorials,
  loadUserPage,
  loadViewPDF,
  loadVipStatus,
  prefetchDashboardRoutes,
} from "./routes/prefetch";

const Home = lazy(() => import("./pages/home"));
const DashboardAppPage = lazy(loadDashboardAppPage);
const CommunityNotificationsPage = lazy(loadCommunityNotificationsPage);
const Apps = lazy(loadApps);
const Cart = lazy(loadCart);
const Scopes = lazy(loadScopes);
const Page404 = lazy(() => import("./pages/Page404"));
const Settings = lazy(loadSettings);
const Orders = lazy(loadOrders);
const SimpleLayout = lazy(() => import("./layouts/simple"));
const UserPage = lazy(loadUserPage);
const Profile = lazy(loadProfile);
const DashboardLayout = lazy(loadDashboardLayout);
const SignupPage = lazy(() => import("./pages/auth/register/Signup"));
const CatalogPage = lazy(loadCatalogPage);
const PublicCatalogPage = lazy(() =>
  import("./pages/catalog/PublicCatalogPage"),
);
const PrivacyPolicy = lazy(() => import("./pages/home/PrivacyPolicy"));
const ConsentScreen = lazy(() => import("./pages/oAuth/ConsentScreen"));
const TermsOfService = lazy(() => import("./pages/home/TermsOfService"));
const ProductsPage = lazy(loadProductsPage);
const ProductPage = lazy(loadProductPage);
const ForgotPassword = lazy(() =>
  import("./pages/auth/forgot-password/ForgotPassword"),
);
const ShipstationCredentials = lazy(loadShipstationCredentials);
const SubscriptionManagement = lazy(loadSubscriptionManagement);
const Trainings = lazy(loadTrainings);
const TrainingsDetail = lazy(loadTrainingsDetail);
const TutorialDetail = lazy(loadTutorialDetail);
const Tutorials = lazy(loadTutorials);
const Stores = lazy(loadStores);
const GrowthMaterial = lazy(loadGrowthMaterial);
const ViewPDF = lazy(loadViewPDF);
const BreakdownPage = lazy(loadBreakdownPage);
const MockupLibrary = lazy(loadMockupLibrary);
const AllisonPage = lazy(() => import("./pages/allison"));
const CohortPage = lazy(() => import("./pages/cohort"));
const DesignPage = lazy(() => import("./pages/design"));
const CountdownAdmin = lazy(loadCountdownAdmin);
const Invoices = lazy(loadInvoices);
const VipStatus = lazy(loadVipStatus);

const PageLoader = () => <ICPPageLoader />;

const LazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

export default function Router() {
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const userType = useSelector((state) => state.user.user?.type);
  const location = useLocation();
  const isAllison =
    new URLSearchParams(location.search).get("via") === "allison";
  const linkedOrderId = readOrderIdQuery(location.search);

  // Logged-out visits keep the id until seller login, including /login?order_id=.
  useLayoutEffect(() => {
    if (!linkedOrderId || isAuthenticated) return;
    rememberSellerOrderDeepLink(linkedOrderId);
  }, [linkedOrderId, isAuthenticated]);

  const dashboardHome =
    linkedOrderId && isSeller(userType)
      ? sellerOrdersDeepLink(linkedOrderId)
      : userType === "AdminAccessGuard"
        ? "/dashboard/user"
        : shouldRedirectToOrdersOnLogin(userType)
          ? "/dashboard/orders"
          : "/dashboard/app";

  const isIncompleteSignup = localStorage.getItem("registered") === "true";
  const needsPayment = isIncompleteSignup;

  const guestOnly = (element) =>
    isAuthenticated && !needsPayment ? (
      <Navigate to={dashboardHome} replace />
    ) : (
      element
    );

  // Authenticated sessions: warm dashboard chunks ASAP (refresh / deep link).
  useEffect(() => {
    if (isAuthenticated) {
      prefetchDashboardRoutes();
    }
  }, [isAuthenticated]);

  // On login screens, download the destination dashboard while credentials are typed.
  useEffect(() => {
    if (location.pathname === "/login") {
      prefetchDashboardRoutes();
    }
    if (location.pathname === "/vips/login") {
      preloadVipsDashboard();
    }
  }, [location.pathname]);

  const routes = useRoutes([
    {
      path: "/",
      element:
        !isAuthenticated && linkedOrderId ? (
          <Navigate to={loggedOutOrderLinkPath(linkedOrderId)} replace />
        ) : (
          guestOnly(
            <LazyRoute>{isAllison ? <AllisonPage /> : <Home />}</LazyRoute>,
          )
        ),
      index: true,
    },

    {
      path: "/dashboard",
      element: !isAuthenticated ? (
        <Navigate to={getLoginPath()} />
      ) : needsPayment ? (
        <Navigate to="/signup/subscribe" replace />
      ) : (
        <LazyRoute>
          <DashboardLayout />
        </LazyRoute>
      ),
      // Page Suspense lives in DashboardLayout so nav stays mounted and soft-loads content.
      children: [
        { element: <Navigate to={dashboardHome} />, index: true },
        { path: "app", element: <DashboardAppPage /> },
        { path: "notifications", element: <CommunityNotificationsPage /> },
        { path: "shipstation", element: <ShipstationCredentials /> },
        { path: "user", element: <UserPage /> },
        { path: "products/:orderID?", element: <ProductsPage /> },
        { path: "product/:id/:orderID?", element: <ProductPage /> },
        { path: "catalog/:orderID?", element: <CatalogPage /> },
        { path: "stores", element: <Stores /> },
        { path: "settings", element: <Settings /> },
        { path: "profile", element: <Profile /> },
        { path: "orders", element: <Orders /> },
        { path: "checkout", element: <Cart /> },
        { path: "scopes", element: <Scopes /> },
        { path: "apps", element: <Apps /> },
        { path: "trainings", element: <Trainings /> },
        { path: "trainings/:id", element: <TrainingsDetail /> },
        { path: "tutorials", element: <Tutorials /> },
        { path: "tutorials/:id", element: <TutorialDetail /> },
        { path: "growth-material", element: <GrowthMaterial /> },
        { path: "growth-material/:id", element: <ViewPDF /> },
        { path: "mockup-library", element: <MockupLibrary /> },
        { path: "design", element: <DesignPage /> },
        {
          path: "subscription-management",
          element: <SubscriptionManagement />,
        },
        { path: "breakdown", element: <BreakdownPage /> },
        {
          path: "partner-breakdown",
          element: <Navigate to="/dashboard/breakdown" replace />,
        },
        {
          path: "icp-breakdown",
          element: <Navigate to="/dashboard/breakdown" replace />,
        },
        { path: "live-session", element: <CountdownAdmin /> },
        { path: "order-invoice", element: <Invoices /> },
        { path: "vip-stats", element: <VipStatus /> },
        { path: "vip-status", element: <VipStatus /> },
      ],
    },

    {
      path: "login",
      element: guestOnly(<LoginPage />),
    },

    {
      path: "vips/login",
      element: guestOnly(<VipLoginPage />),
    },

    {
      path: "products",
      element: guestOnly(
        <LazyRoute>
          <PublicCatalogPage />
        </LazyRoute>,
      ),
    },

    {
      path: "cohort",
      element: (
        <LazyRoute>
          <CohortPage />
        </LazyRoute>
      ),
    },

    {
      path: "design",
      element: <Navigate to="/dashboard/design" replace />,
    },

    {
      path: "signup/:mode?",
      element:
        isAuthenticated && !needsPayment ? (
          <Navigate to={dashboardHome} replace />
        ) : (
          <LazyRoute>
            <SignupPage />
          </LazyRoute>
        ),
    },

    {
      path: "reset-password",
      element: guestOnly(
        <LazyRoute>
          <ForgotPassword />
        </LazyRoute>,
      ),
    },
    {
      element: (
        <LazyRoute>
          <SimpleLayout />
        </LazyRoute>
      ),
      children: [
        {
          element: (
            <Navigate
              to={
                isAuthenticated && !needsPayment
                  ? dashboardHome
                  : getLoginPath()
              }
            />
          ),
        },
        {
          path: "404",
          element: (
            <LazyRoute>
              <Page404 />
            </LazyRoute>
          ),
        },
        { path: "*", element: <Navigate to="/404" /> },
      ],
    },
    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },

    {
      path: "termsofservice",
      element: (
        <LazyRoute>
          <TermsOfService />
        </LazyRoute>
      ),
    },
    {
      path: "privacypolicy",
      element: (
        <LazyRoute>
          <PrivacyPolicy />
        </LazyRoute>
      ),
    },

    {
      path: "/app/oauth2/auth",
      element: (
        <LazyRoute>
          <ConsentScreen />
        </LazyRoute>
      ),
    },
  ]);

  return routes;
}
